import { useEffect, useMemo, useRef } from "react";

const DEFAULTS = {
  announce_delay_reminder: true,
  delay_initial_minutes: 10,
  delay_repeat_minutes: 5,
};
const MAX_DELAY_ANNOUNCEMENTS_PER_STATE = 2;

const toDateMs = (value) => {
  const ts = new Date(value).getTime();
  return Number.isFinite(ts) ? ts : Date.now();
};

export const buildRhodeDelayPayload = (order = {}, elapsedMinutes = 0) => ({
  announcementKey:
    String(order?.dispatchKind || "").toLowerCase() === "claim" &&
    Number(order?.dispatchEventId) > 0
      ? `dispatch:${Number(order.dispatchEventId)}`
      : String(order.id || ""),
  tableNumber: order.table,
  orderId: order.id,
  elapsedMinutes,
  context: order.context || null,
  orderType: order.orderType || order.order_type || null,
  isDelivery: Boolean(order.isDelivery || order.is_delivery),
  deliveryMode: order.deliveryMode || order.delivery_mode || null,
  deliveryMethod: order.deliveryMethod || order.delivery_method || null,
  courseId: order.courseId || order.course_id || null,
  courseNumber: order.courseNumber || order.course_number || null,
  serverName: order.serverName || order.server_name || null,
  dispatchKind: order.dispatchKind || order.dispatch_kind || null,
  dispatchEventId: order.dispatchEventId || order.dispatch_event_id || null,
  dispatchActorName:
    order.dispatchActorName || order.dispatch_actor_name || null,
  items: Array.isArray(order.items) ? order.items : [],
});

export const isRhodeDelayEligible = (order = {}) => {
  if (String(order?.dispatchKind || "").toLowerCase() === "claim") {
    return (order?.items || []).some(
      (item) =>
        !["served", "cancelled"].includes(
          String(item?.status || "").toLowerCase(),
        ),
    );
  }
  if (
    !["pending", "confirmed"].includes(
      String(order?.status || "").toLowerCase(),
    )
  ) {
    return false;
  }
  const courseNumber = Number(order?.courseNumber || order?.course_number || 1);
  if (courseNumber <= 1) return true;
  return !(order?.items || []).some((item) =>
    ["preparing", "ready", "served"].includes(
      String(item?.status || "").toLowerCase(),
    ),
  );
};

export const buildRhodeDelayTrackingKey = (
  order = {},
  interfaceType = "cuisine",
) => {
  const payload = buildRhodeDelayPayload(order);
  return `${interfaceType}-${payload.announcementKey || order?.id || "unknown"}`;
};

export const shouldEmitRhodeDelayReminder = (
  state,
  nowMinutes,
  repeatMinutes,
) => {
  const count = Number(state?.count || 0);
  if (count >= MAX_DELAY_ANNOUNCEMENTS_PER_STATE) return false;
  if (!Number.isFinite(Number(state?.lastAtMinute))) return true;
  return (
    Number(nowMinutes) - Number(state.lastAtMinute) >= Number(repeatMinutes)
  );
};

const useRhodeDelayAlerts = (
  orders,
  rhodeRef,
  interfaceType = "cuisine",
  remoteConfig = null,
  rhodeActive = true,
) => {
  const alertedMapRef = useRef(new Map());
  const intervalRef = useRef(null);
  const rhodeActiveRef = useRef(false);

  const cfg = useMemo(() => {
    const merged = { ...DEFAULTS, ...(remoteConfig || {}) };
    return {
      announce_delay_reminder: Boolean(merged.announce_delay_reminder),
      delay_initial_minutes: Math.max(
        1,
        Number(merged.delay_initial_minutes || 10),
      ),
      delay_repeat_minutes: Math.max(
        1,
        Number(merged.delay_repeat_minutes || 5),
      ),
    };
  }, [remoteConfig]);

  const getElapsedMinutes = (timestamp) =>
    Math.floor((Date.now() - toDateMs(timestamp)) / 60000);

  const checkDelayAlerts = ({ baselineOnly = false } = {}) => {
    if (!cfg.announce_delay_reminder) return;
    const nowMinutes = Math.floor(Date.now() / 60000);
    const eligibleOrders = (orders || []).filter((order) =>
      isRhodeDelayEligible(order),
    );
    const activeKeys = new Set(
      eligibleOrders.map((order) =>
        buildRhodeDelayTrackingKey(order, interfaceType),
      ),
    );
    for (const key of alertedMapRef.current.keys()) {
      if (!activeKeys.has(key)) {
        alertedMapRef.current.delete(key);
      }
    }

    const audioEnabled = Boolean(
      rhodeActive && rhodeRef.current?.isEnabled?.(),
    );
    if (!audioEnabled || baselineOnly) {
      eligibleOrders.forEach((order) => {
        const elapsed = getElapsedMinutes(order.timestamp || order.created_at);
        if (elapsed >= cfg.delay_initial_minutes) {
          alertedMapRef.current.set(
            buildRhodeDelayTrackingKey(order, interfaceType),
            { lastAtMinute: nowMinutes, count: 0 },
          );
        }
      });
      return;
    }

    eligibleOrders.forEach((order) => {
      const elapsed = getElapsedMinutes(order.timestamp || order.created_at);
      if (elapsed < cfg.delay_initial_minutes) return;

      const key = buildRhodeDelayTrackingKey(order, interfaceType);
      const alertState = alertedMapRef.current.get(key);
      const shouldRemind = shouldEmitRhodeDelayReminder(
        alertState,
        nowMinutes,
        cfg.delay_repeat_minutes,
      );

      if (shouldRemind) {
        alertedMapRef.current.set(key, {
          lastAtMinute: nowMinutes,
          count: Number(alertState?.count || 0) + 1,
        });
        rhodeRef.current.speakDelayAlert(
          buildRhodeDelayPayload(order, elapsed),
        );
      }
    });
  };

  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    const justActivated = Boolean(rhodeActive) && !rhodeActiveRef.current;
    rhodeActiveRef.current = Boolean(rhodeActive);
    checkDelayAlerts({ baselineOnly: justActivated });
    intervalRef.current = setInterval(checkDelayAlerts, 60 * 1000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders, cfg, interfaceType, rhodeActive]);

  const getDelayStatus = (order) => {
    const elapsed = getElapsedMinutes(order.timestamp || order.created_at);
    if (elapsed >= cfg.delay_initial_minutes + 10) {
      return { level: "critical", color: "red", message: "Très en retard" };
    }
    if (elapsed >= cfg.delay_initial_minutes) {
      return { level: "warning", color: "orange", message: "En retard" };
    }
    if (elapsed >= Math.max(1, cfg.delay_initial_minutes - 3)) {
      return { level: "caution", color: "yellow", message: "Attention" };
    }
    return { level: "normal", color: "green", message: "Dans les temps" };
  };

  return {
    getElapsedMinutes,
    isOrderDelayed: (order) =>
      getElapsedMinutes(order.timestamp || order.created_at) >=
      cfg.delay_initial_minutes,
    getDelayStatus,
  };
};

export default useRhodeDelayAlerts;
